import { ImportResult } from '../converters/types/converters.types';
import { SpotWeather } from '../utils/route-weather-worker';
import { ShipModel } from './ship-model';

export enum LegType {
    RhumbLine = "Rhumbline",
    GreatCircle = "GreatCircle"
}

export enum GeometryType {
    Point = "Point",
    LineString = "LineString"
}

export enum RoutingModelEvents {
    calculationCompleted = "route-successfully-calculated"
}
export interface WeatherLimitValue
{
    avoid: number|null;
    warn: number|null;
}

export interface WeatherLimitPeriodValue
{
    avoid: { max: number|null, min: number|null };
    warn: { max: number|null, min: number|null };
}

export interface WeatherLimits {
    Waves: WeatherLimitValue;
    SwellHeight: WeatherLimitValue;
    HeadSwellHeight: WeatherLimitValue;
    HeadSwellPeriod: WeatherLimitPeriodValue;
    BeamSwellHeight: WeatherLimitValue;
    BeamSwellPeriod: WeatherLimitPeriodValue;
    FollowingSwellHeight: WeatherLimitValue;
    FollowingSwellPeriod: WeatherLimitPeriodValue;
    Wind10M: WeatherLimitValue;
    Wind50M: WeatherLimitValue;
    Visibility: WeatherLimitValue;
    Icing: WeatherLimitValue;
    Hurricanes: WeatherLimitValue;
    TropicalStorms: WeatherLimitValue;
    Depressions: WeatherLimitValue;
    WindGusts10M: WeatherLimitValue;
    WindGusts50M: WeatherLimitValue;
    RiskWindSpeed: WeatherLimitValue;
    RiskWaveHeight: WeatherLimitValue;
    HeadWind: WeatherLimitValue;
    BeamWind: WeatherLimitValue;
    FollowingWind: WeatherLimitValue;
    BeamWavesHeight: WeatherLimitValue;
    BeamWavesPeriod: WeatherLimitPeriodValue;
    HeadWavesHeight: WeatherLimitValue;
    HeadWavesPeriod: WeatherLimitPeriodValue;
    FollowingWavesHeight: WeatherLimitValue;
    FollowingWavesPeriod: WeatherLimitPeriodValue;
    IceConcentration: WeatherLimitValue;
    MaxRollAngle: WeatherLimitValue;
}

export interface WaypointUserInput {
    max_x_track?: number;
    min_depth?: number;
    catzoc?: string;
    rate_of_turn?: number;
    turn_radius?: number;
    tide?: number;
    speed_maximum?: number;
    speed_minimum?: number;
}

export interface RoutePassagePlanUserInput {
    weatherInfoNavtexStations?: string;
    safetyMessagesNavtexStations?: string;
    safetyMessagesNavareaRegions?: string;
    weatherInfoNavareaRegions?: string;
    navareaRegions?: string;
    sailingDirections?: string;
}

export enum FeatureOrigin {
    System = 'system', //BPR
    Bridge = 'bridge', //USER
    Zone = 'zone', //ECA/SECA zones & Whale zones 
}

export interface ShallowSounding
{
    coords: [number, number]; // [lon, lat]
    depth: number;
    cell_info?: string;
    catzoc: string;
    sourceType?: string; // depth_source.type, e.g. "sounding" | "dredged_area"
    ukc?: number; // Under Keel Clearance computed for this depth, in meters
}

export interface DepthSource
{
    type: string; // e.g. "sounding" | "dredged_area"
    coords: [number, number]; // [lon, lat]
    cell_info?: string;
}

export interface VoyageRoutePointFeature
{
    type: "Feature";
    geometry: {
        coordinates: [number,number];
        type: "Point";
    };
    properties: {
        [index:string]: any;
        eta?: string; // User input - for points where we need to arrive at a specific time (usually for pilot points)
        is_stoppage_point?: boolean; // Stoppage point
        stoppage_end?: boolean; // Marks the end waypoint of a stoppage pair
        etd?: string; // Stoppage waypoint indentifier
        stoppageReason? : string; // Stoppage
        comment? : string; // Comment
        pilotPoint?: boolean;
        speed_maximum?: number; // Maximum speed restriction (in m/s)
        speed_minimum?: number; // Minimum speed restriction (in m/s)
        type?: "Hard"|"Navigational"|"Weather"|"Informational"; // "Weather" is only possible when using DTNs routing
        isPassed?: boolean; // Whether this waypoint has already been passed
        vesselPositionAt?: string; // This waypoint was injected as the vessels current position at the given timestamp

        // Below properties should be present for a fully calculated route
        timestamp?: string; // calculated time at this point

        // Below properties may be added for the passage plan
        turnrate?: string;
        name?: string; // User defined name/details for this waypoint
        catzoc?: string; // Category Zone of Confidence
        lop?: string // Line of Position (LOP) interval
        max_x_track?: number; // Max cross track error
        min_depth?: number; // Minimum depth in meters
        x_mark?: [number, number]; // [lon, lat] of hazard determining XTD width
        depth_mark?: [number, number]; // [lon, lat] of where min_depth was measured
        rate_of_turn?: number; // Rate of turn in degrees per minute
        turn_radius?: number; // Turn radius
        tide?: number; // Tide value in meters
        squat?: number; // Squat in meters
        ukc?: number; // Under Keel Clearance in meters
        userInput?: WaypointUserInput;
        // openWaters is used for squat calculation - it determines whether the waterway is confined (ie. shallow and/or narrow) or open
        // Default value is false (ie. confined) for berth->pilot and pilot->berth, true (ie. open) for pilot->pilot
        openWaters?: boolean;
        origin?: FeatureOrigin;
        // Below properties are related to the parametric roll calculation
        // Will only be present on "real" points when no informational waypoints are present on the leg, otherwise
        // they are present in the informational waypoints
        parametric_roll_ratio_wave?: number // Ratio between natural roll period and wave encounter period
        parametric_roll_ratio_swell?: number // Ratio between natural roll period and swell encounter period
        is_high_risk_roll_wave?: boolean // Is risk of parametric roll high, based on parametric_roll_ratio_wave
        is_high_risk_roll_swell?: boolean // Is risk of parametric roll high, based on parametric_roll_ratio_swell
        is_high_risk_roll?: boolean // True if either is_high_risk_roll_wave or is_high_risk_roll_swell
        roll_risk_info?: RollRiskInfo|RollRiskInfo[]
        wr_timestamp?: string; // Timestamp from WNI
        change_speed?: string; // WNI speed change flag

        eta_deviation?: {
            eta: string;
            delay: number;
        }

        userInserted?: boolean; // used to determine whether the navigational waypoint was inserted by the user
    };
}

export interface WeatherSummary
{
    max: { value: number; direction?: number; };
    min: { value: number; direction?: number; };
    average: { value: number; direction?: number; };
}

export interface SubscriptionInfo
{
    forecast: string;
    importedAt: string;
    id: string;
}

export interface WarningSegment
{
    actual: number;
    coordinates: [number,number][];
}

export interface WeatherWarning
{
    type: keyof WeatherLimits;
    limit: number;
    actual?: number; // Old format
    segments?: WarningSegment[]; // New format
}

export type RollRiskType = "high_roll_risk" | 'parametric_roll_risk' | 'transverse_acceleration_risk';
export interface RollRiskInfo {
    risk_type: RollRiskType
}
export interface ParametricRollWarning
{
    coordinates: [number,number][]; // start and end coordinates
    timeStart: string; // Time at start coordinates
    timeEnd: string; // Time at end coordinates
    roll_risk_info?: RollRiskInfo|RollRiskInfo[]
}

export interface VoyageRouteLineFeature
{
    type: "Feature";
    geometry: {
        coordinates: [number,number][];
        type: "LineString";
    };
    properties: {
        [index:string]: any;

        // Below properties should be present for a fully calculated route
        course?: number; // Not present for GreatCircle legs
        leg_type?: "Rhumbline"|"GreatCircle";
        distance?: number; // in km
        speed_over_ground?: number; // in m/s
        speed_through_water?: number; // in m/s
        propulsion_power?: number; // in kW
        propulsion_power_lower?: number; // in kW
        propulsion_power_upper?: number; // in kW
        roll_angle?: number;
        main_engine_rpm?: number;
        main_engine_rpm_lower?: number;
        main_engine_rpm_upper?: number;
        weather?: { [index:string]: WeatherSummary };
        warnings?: {
            weather: {
                warn: WeatherWarning[];
                avoid: WeatherWarning[];
            };
            parametricRoll?: ParametricRollWarning[];
        };
        informationalWaypoints?: VoyageRoutePointFeature[];
        spotWeathers?: SpotWeather[]

        // Below properties may be present when the leg was enriched with ENC depth data
        min_depth?: number; // Minimum depth in meters along this leg
        catzoc?: string; // Category Zone of Confidence
        max_x_track?: number; // Max cross track error
        enc_provenance?: string; // Human-readable description of where min_depth/depth_source came from
        depth_mark?: [number, number]; // [lon, lat] of where min_depth was measured
        drval1?: number;
        drval2?: number;
        soundings?: { coords: [number, number]; depth: number; cell_info?: string; catzoc: string }[];
        depth_source?: DepthSource;
    };
}

export interface RoutePassagePlanProperties {
    description: string,
    reminders: string
    notEcdisCompliant: boolean,
    encCellsUsed: string,
    booksAndTables: string,
    chartsAndLightsNotes: string,
    sailingDirections: string,
    listOfRadioSignals: string,
    listOfLights: string,
    gpsEcdisRadarMap: string,
    paperCharts: string,
    pubCorrectedToWeek: string,
    tideTables: string,
    publicationsNotes: string,
    mastersOrders: string,
    safetyMessages: {
        navareaRegions: string,
        navtexStations: string
    },
    weatherInfo: {
        navareaRegions: string,
        navtexStations: string,
        info: string
    },
    equipmentStatus: {
        operationalCondition: string,
        failures: string
    },
    weatherRouting: {
		required: boolean,            
		acknowledged: boolean,
		pilotPointAcknowledged: boolean 
	}
    captain: string;
    navigationalOfficer: string;
    planDate: string | null;
    userInput?: RoutePassagePlanUserInput;
}

export type RouteGeometry = (VoyageRouteLineFeature|VoyageRoutePointFeature)[]

export type VoyageRouteWeather = {
    forecast_versions: string[];
    subscriptions?: SubscriptionInfo[];
    summary: { [index:string ]: WeatherSummary };
    hasLowResData?: boolean;
    hasHighResData?: boolean;
    weatherPoints?: VoyageRoutePointFeature[];
}

export type VoyageRouteTotal = {
    non_eca: {
        time: number; // in hours, format: total hours; describes total route time sailed through regular areas
        fuel_cost: number; // monetary value of total regular fuel cost to cover full route without any currency reference
        vessel_cost: number; // monetary value without any currency reference
        distance: number; // in km (available for ShortestDistance), describes total route distance to be sailed through regular areas with regular fuel
        fuel: number; // in mT, describes total amount of fuel to be utilized during calculated route (fuel consumption of main engine)
        fuel_lower?: number; // in mT, lower bound of fuel consumption
        fuel_upper?: number; // in mT, upper bound of fuel consumption
    },
    eca: { // not filled in until ECA support; same as above but with light fuel and via ECA areas
        time: number;
        fuel_cost: number;
        vessel_cost: number;
        distance: number;
        fuel: number;
        fuel_lower?: number; // in mT, lower bound of fuel consumption
        fuel_upper?: number; // in mT, upper bound of fuel consumption
    }
}

export type WeatherRoutingInfo = {
    id?: number;
    required: boolean;
    required_reason?: string; // Admin, BPR, HeavyWeather, RiskRoute, Manual
    status?: string; // Unread, read, accept, reject, overwritten/deactive
    requested_at?: string; // ISO 8601 format
    updated_at?: string; // ISO 8601 format
    updated_at_reason?: string; // RTA Change, Regular Update, etc.
    updated_at_reason_type?: string; // Auto or Manual
    received_at?: string; 
    audit_id?: string;
    correlationId?: string;
    remarks?: string;
    submitted_at?: string;
    is_original?: boolean;
  }

export enum WniRequiredReason {
    Admin = "Admin",
    BPR = "BPR",
    HeavyWeather = "HeavyWeather",
    RiskRoute = "RiskRoute",
    Manual = "Manual"
}

// 'postDeparture': approved more than a day after ETD, or after ETA, when the reason is no longer collected.
export type BprPopupSkipReason = 'aligned' | 'fuelEfficient' | 'noBpr' | 'postDeparture';

export interface BprAdoption {
    // From route notification (always populated on approve)
    isAlignedToStarRoute: boolean;
    isMoreFuelEfficientThanStarRoute: boolean;
    isWrAdviseReceived: boolean;
    isBPRCalculationCompleted: boolean;
    bprIDsInScheduleEntry: string[];
    routeDistanceNm: number;
    routeFuelConsumption: number;

    // Whether the exceed-limits popup was shown to the user
    popupShown: boolean;
    // Why it was skipped (only when popupShown = false)
    popupSkipReason?: BprPopupSkipReason;

    // From popup submission (only when user chose "Continue with own route")
    reason?: string;
    comment?: string;
}

export interface VoyageRoute
{
    routeId: string;
    name?: string;
    uiType?: string;
    routeCreatedBy?: "system"|"WR"|"bridge"; // system = BPR, WR = WNI advice, bridge = user route
    creationTime?: string;
    bprAlignment?: {
        isAligned: boolean;
        bprId: string;
    };
    bprAdoption?: BprAdoption;
    isPassedUpdate?: string; // time for last update of isPassed property on route_geometry
    bestPracticeRouteId?: string; //add best practice route id
    route_info?: {
        eta: string; // calculated value, valid for shortestWeatherOptimized and weatherOptimized route types
        etd: string;
        speed_over_ground: number; // in m/s, describes speed used to calculate the route
        co2: number; // in mT, describes calculated CO2 emission
        totals: VoyageRouteTotal,
        rpm: number;
        weather: VoyageRouteWeather;
        calculationInput: {
            forecast_versions: string[];
            draftFore: number;
            draftAft: number;
            gmt?: number;
            etd: string;
            eta: string;
            weatherLimits: WeatherLimits;
        };
        hydraRequestId?: string;
        hydraErrors?: any[];
        whaleZonesApplied?: boolean;
        // Set by the "Disable whale zone speed restrictions" route action. Analytics applies the
        // restrictions by default, so this is true only when the user opted out; the routeoptimiser
        // then sends the whale zone to the constant-power calculation as zones_to_ignore.
        disableWhaleZoneSpeedRestrictions?: boolean;
        // Set when the user has stepped through the route's precautionary zone entry/exit waypoints
        // in the route editor and clicked "Mark as reviewed". Until then the route counts as needing
        // review and "Select route" / "Passage Plan" stay blocked. Written on Save & Exit and carried
        // across a recalculation only while the route crosses the very same zone points, so new
        // geometry always has to be reviewed again. See getZoneCrossingSignature.
        routeWarningsReviewed?: boolean;
        warnings?: (string | { message: string; paths?: string[] })[];
    };
    route_geometry: RouteGeometry,
    passagePlan?: RoutePassagePlanProperties;
    routeIdParent?: string|null;
    weatherRoutingInfo?: WeatherRoutingInfo;
    recalcNeeded?: boolean; // Whether the route needs to be recalculated
}

export interface UpdatePlanOptions
{
    routeIndex: number; // -1 to add as a new route
    autoApprove?: boolean;
    selectRoute?: boolean;
    selectedRouteIndex?: number;
}

export interface CalculationFailedDetails
{
    error: string;
    scheduleId?: string;
    hydraRequestId?: string;
    hydraErrorBody?: any;
    routeIndex?: number;
}

export interface NextWaypointPayload {
    routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>;
    position: number[];
    maxAllowedDeviation: number;
}


export interface NextWaypointResult {
    input: NextWaypointPayload;
    result: string;
    nextWPIndex: number;
    deviation: number;
}

export interface RoutingJobOptions
{   
    routeId: string;
    draft?: number; // Either draft or draftAft and draftFore needs to be present
    draftAft?: number;
    draftFore?: number;
    routeType?: "ShortestDistance"|"ShortestWeatherOptimized"|"WeatherOptimized";
    arrivalPilotEta?: number;
    departurePilotEtd?: number;
    shipModel?: ShipModel;
    uiRouteIndex?: number;
    etd?: number;
    eta?: number;
    rollAngle?: number | null; // in degrees, used for parametric roll calculation
    weatherLimits?: WeatherLimits;
    forecastVersion?: string;
    encValidation?: boolean;
    numberOfReefers?: number;
    reeferConsumption?: number;
    dangerousCargo?: boolean;
    updatePlan?: UpdatePlanOptions;
    planScheduleId?: string;
    addRouteToModel?: boolean;
    maxSegmentDistance?: number;
    gmt?: number;
    min_depth?: number;
    catzoc?: string;
    max_x_track?: number;
    rate_of_turn?: number;
    turn_radius?: number;
    solidGm?: number;

    // Override ship dimensions, margins and speed (for testing purposes)
    airDraft?: number;
    lpp?: number;
    beam?: number;
    blockCoefficient?: number;
    aftMargin?: number;
    airMargin?: number;
    draftMargin?: number;
    forwardMargin?: number;
    portsideMargin?: number;
    starboardMargin?: number;
    minSpeed?: number;
    maxSpeed?: number;
    imo?: number;
    whaleZonesApplied?: boolean;
    // True only when the user disabled the whale zone speed restrictions for the route being
    // calculated; forwarded to the routeoptimiser, which turns it into zones_to_ignore.
    disableWhaleZoneSpeedRestrictions?: boolean;
    passagePlan?: RoutePassagePlanProperties;
    recalcParametersUpdate?: boolean; //here recalc parameters refers to eta, etd, gmt, draft, gmt etc
    weatherRoutingInfo?: WeatherRoutingInfo;
    oldPlanArrivalPilotEta?: number;
    shouldRecalculate?: boolean;
    updatePlanEta?: boolean;
}

export type RouteCalcStatus = "idle"|"calculating"|"failed";

export interface UIRouteWithMetadata
{
    routeId: string;
    status: RouteCalcStatus;
    route: VoyageRoute|null;
    error: string|null;
    selected: boolean;
    crossingWhaleZones: boolean;
}

export interface FailedUIRouteWithMetadata extends UIRouteWithMetadata {
    index: number;
}

export interface PositionAndHeadingInTime
{
    position: [number, number];
    projectedPosition: [number, number]
    heading: number;
}

export enum Backends
{
    DTN = "DTN",
    EncApi = "encApi",
    RouteOptimiser = "RouteOptimiser",
    Hydra = "Hydra"
}

export interface MissingPoints {
    beforeFistPilotPoint: (VoyageRoutePointFeature | VoyageRouteLineFeature)[] | undefined;
    afterLastPilotPoint: (VoyageRoutePointFeature | VoyageRouteLineFeature)[] | undefined;
}

export abstract class VoyageRoutingModelBase extends EventTarget
{
    abstract shipModel: ShipModel;
    abstract get jobCount(): number;
    abstract get routes(): VoyageRoute[];
    abstract get uiRoutes(): UIRouteWithMetadata[];
    abstract get activePlan(): string|null;
    abstract get routingBackend(): Backends;

    abstract changeRoutingBackend(newBackend: Backends): void;
    abstract setActivePlan(scheduleId: string|null): Promise<void>;
    abstract addRoute(route: VoyageRoute): number;
    abstract getRoute(index: number): VoyageRoute;
    abstract deleteRoute(index: number): void;
    abstract postRoutingJob(startPos: [number, number], endPos: [number, number], options: RoutingJobOptions): Promise<VoyageRoute>;
    abstract postRoutingJobUsingRoute(route: VoyageRoute, options: RoutingJobOptions, noVerifyEtaPopUp?: boolean): Promise<VoyageRoute>;
    abstract postEncAugmentRouteJob(route: VoyageRoute): Promise<VoyageRoute|null>;
    abstract getNextWaypointIndex(maxAllowedDeviation: number, position: number[], routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): Promise<NextWaypointResult>;

    abstract setWeatherLimits(weatherLimits: WeatherLimits): void;
    abstract getWeatherLimits(): WeatherLimits;

    abstract planHasActiveCalculations(scheduleId: string): boolean;


    abstract importFilesRaw(files: FileList): Promise<ImportResult[]>;
    abstract importFile(file: File, options: RoutingJobOptions, missingPoints: MissingPoints | null, mergeWith?: VoyageRoute, addCurrentPosition?: boolean): Promise<VoyageRoute>;
    abstract importShipModelFile(file: File): Promise<void>;

    abstract getPositionAndHeadingInTime(timestamp: number, route: VoyageRoute): PositionAndHeadingInTime|null;
    abstract getEtaAtPosition(projectedCoord: number[], route: VoyageRoute): number|null;
    abstract saveRoute(voyageRoute: VoyageRoute, uiRouteIndex: number, sceduleId: string, routeId: string): Promise<void>;

    abstract setEtaDeviationPopupIsShown(scheduleId: string, routeIndex: number, status: "close"|"open"): void;
    abstract setWarningPopupIsShown(scheduleId: string, routeIndex: number, status: "close"|"open"): void;
    abstract etaDeviationPopupIsShown(scheduleId: string, routeIndex: number): boolean;
    abstract warningPopupIsShown(scheduleId: string, routeIndex: number): boolean;
    abstract _setRouteViewStatus(routeId:String,scheduleId: string, hide: boolean): void;
    abstract _getHiddenRoutes(): string[];
}

let VoyageRoutingModel: VoyageRoutingModelBase|null = null;

export function setVoyageRoutingModel(model: VoyageRoutingModelBase)
{
    VoyageRoutingModel = model;
}

export { VoyageRoutingModel };
